import { GripHorizontal } from 'lucide-react'
import { Resizable } from 're-resizable'
import {
  ReactNode,
  RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from 'react'
import { cn } from '~/@/lib/utils'

const VIEWPORT_MARGIN = 8
const MIN_WIDTH = 420
/** Matches the old popover's `minWidth: min(500px, ...)` so the default width is unchanged. */
const DEFAULT_WIDTH = 500
const MIN_HEIGHT = 320
const DEFAULT_HEIGHT = 560
/** Keep at least this many pixels of the frame on-screen so it can never be dragged fully away. */
const KEEP_VISIBLE = 48
/** Vertical gap between the anchor's bottom edge and the frame's top on first open. */
const ANCHOR_GAP = 4

interface Size {
  width: number
  height: number
}

interface Position {
  top: number
  left: number
}

interface DraggableResizablePopoverProps {
  /** Trigger element the frame is initially positioned beneath (bottom-start). */
  anchorRef: RefObject<HTMLElement | null>
  /** True while the picker is open; drives (re)initializing position/size. */
  open: boolean
  children: ReactNode
  className?: string
}

/**
 * A fixed-position popover frame the user can drag (via the top grip) and resize (right/bottom/
 * corner). Used for the lookup picker only when the Filter & sort panel is available, so the small
 * default popover experience is unchanged. Positions itself under the anchor on open.
 */
export const DraggableResizablePopover = ({
  anchorRef,
  open,
  children,
  className,
}: DraggableResizablePopoverProps) => {
  const [size, setSize] = useState<Size | null>(null)
  const [position, setPosition] = useState<Position | null>(null)
  const initializedRef = useRef(false)

  const clampPosition = useCallback((pos: Position, frameSize: Size): Position => {
    const maxLeft = window.innerWidth - KEEP_VISIBLE
    const maxTop = window.innerHeight - KEEP_VISIBLE
    return {
      left: Math.min(Math.max(KEEP_VISIBLE - frameSize.width, pos.left), maxLeft),
      top: Math.min(Math.max(VIEWPORT_MARGIN, pos.top), maxTop),
    }
  }, [])

  // Position/size the frame under the anchor when it opens; reset when it closes so the next open
  // starts anchored again rather than wherever it was last dragged. useLayoutEffect (not useEffect)
  // so position/size are committed before the browser paints — otherwise the frame flashes at its
  // unpositioned default for one frame before jumping under the anchor.
  useLayoutEffect(() => {
    if (!open) {
      initializedRef.current = false
      setSize(null)
      setPosition(null)
      return
    }
    if (initializedRef.current) {
      return
    }
    const rect = anchorRef.current?.getBoundingClientRect()
    const width = Math.min(
      Math.max(rect?.width ?? DEFAULT_WIDTH, DEFAULT_WIDTH),
      window.innerWidth - VIEWPORT_MARGIN * 2,
    )
    const height = Math.min(DEFAULT_HEIGHT, window.innerHeight - VIEWPORT_MARGIN * 2)
    const nextSize = { width, height }
    const top = (rect?.bottom ?? VIEWPORT_MARGIN) + ANCHOR_GAP
    const left = rect?.left ?? VIEWPORT_MARGIN
    // Prefer to keep the whole frame on-screen on first paint.
    const initialLeft = Math.min(left, window.innerWidth - width - VIEWPORT_MARGIN)
    const initialTop = Math.min(top, window.innerHeight - height - VIEWPORT_MARGIN)
    setSize(nextSize)
    setPosition({
      left: Math.max(VIEWPORT_MARGIN, initialLeft),
      top: Math.max(VIEWPORT_MARGIN, initialTop),
    })
    initializedRef.current = true
  }, [open, anchorRef])

  // Holds the teardown for an in-flight drag so we can remove the window listeners if the popover
  // unmounts mid-drag, and so a second pointerdown can't stack a duplicate drag.
  const dragCleanupRef = useRef<(() => void) | null>(null)

  // Detach any lingering drag listeners on unmount.
  useEffect(() => {
    return () => {
      dragCleanupRef.current?.()
    }
  }, [])

  const handleDragStart = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (!position || !size || dragCleanupRef.current) {
        return
      }
      event.preventDefault()
      const startX = event.clientX
      const startY = event.clientY
      const origin = position

      const handleMove = (moveEvent: PointerEvent) => {
        setPosition(
          clampPosition(
            {
              left: origin.left + (moveEvent.clientX - startX),
              top: origin.top + (moveEvent.clientY - startY),
            },
            size,
          ),
        )
      }
      const cleanup = () => {
        window.removeEventListener('pointermove', handleMove)
        window.removeEventListener('pointerup', cleanup)
        dragCleanupRef.current = null
      }
      window.addEventListener('pointermove', handleMove)
      window.addEventListener('pointerup', cleanup)
      dragCleanupRef.current = cleanup
    },
    [position, size, clampPosition],
  )

  if (!size || !position) {
    return null
  }

  return (
    <Resizable
      enable={{ right: true, bottom: true, bottomRight: true }}
      maxHeight={window.innerHeight - VIEWPORT_MARGIN * 2}
      maxWidth={window.innerWidth - VIEWPORT_MARGIN * 2}
      minHeight={MIN_HEIGHT}
      minWidth={MIN_WIDTH}
      onResizeStop={(_event, _direction, _ref, delta) => {
        setSize((prev) =>
          prev ? { width: prev.width + delta.width, height: prev.height + delta.height } : prev,
        )
      }}
      size={size}
      style={{
        position: 'fixed',
        top: position.top,
        left: position.left,
        zIndex: 1300,
        boxShadow:
          '0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12)',
        backgroundColor: '#fff',
        borderRadius: '4px',
      }}
    >
      <div className={cn('flex h-full min-h-0 w-full flex-col overflow-hidden rounded', className)}>
        {/* Drag handle: grab anywhere on this bar to move the popover. */}
        <div
          className="flex shrink-0 cursor-move items-center justify-center border-b border-gray-3 py-1 text-gray-8 hover:text-gray-10"
          onPointerDown={handleDragStart}
        >
          <GripHorizontal size={16} />
        </div>
        {children}
      </div>
    </Resizable>
  )
}
